import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Card, SkeletonRows, EmptyState } from "@/components/dash/ui";
import { AgreementStatusBadge, AgreementTypeBadge } from "@/components/dash/AgreementBadges";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { FileSignature, ArrowLeft, Send, CheckCircle2, Edit3, Ban, RotateCcw, PenLine, AlertTriangle, Lock, ShieldCheck, RefreshCw, ClipboardCheck } from "lucide-react";

const TABS = ["Overview", "Parties", "Clauses", "Versions", "Activity"];

const fmt = (v) => (v === null || v === undefined || v === "" ? null : String(v));
const Field = ({ label, value }) => (
  <div className="py-2 border-b border-slate-50">
    <div className="text-xs uppercase tracking-wide text-slate-400 mb-0.5">{label}</div>
    {fmt(value) ? <div className="text-sm text-slate-800 break-words">{fmt(value)}</div> : <div className="text-sm text-slate-400 italic">Not provided</div>}
  </div>
);

function EditDialog({ open, onClose, agreement, requiredVars, onSaved }) {
  const [form, setForm] = React.useState({});
  const [vars, setVars] = React.useState({});
  const [busy, setBusy] = React.useState(false);
  React.useEffect(() => {
    if (!open) return;
    setForm({ title: agreement.title || "", effective_date: agreement.effective_date || "", expiry_date: agreement.expiry_date || "" });
    const v = {}; (requiredVars || []).forEach((rv) => { v[rv.key] = (agreement.variables || {})[rv.key] ?? rv.default ?? ""; });
    setVars(v);
  }, [open]);
  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/agreements/${agreement.id}`, { ...form, variables: vars });
      toast.success("Agreement updated"); onClose(); onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Edit Draft</DialogTitle><DialogDescription>Editing merge fields re-renders the agreement and creates a new version.</DialogDescription></DialogHeader>
        <div className="space-y-4 mt-2">
          <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1" data-testid="edit-title" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Effective Date</Label><Input type="date" value={form.effective_date} onChange={(e) => setForm({ ...form, effective_date: e.target.value })} className="mt-1" data-testid="edit-effective" /></div>
            <div><Label>Expiry Date</Label><Input type="date" value={form.expiry_date} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} className="mt-1" data-testid="edit-expiry" /></div>
          </div>
          {(requiredVars || []).length > 0 && <div className="pt-2"><div className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">Merge Fields</div>
            <div className="space-y-3">{requiredVars.map((rv) => (
              <div key={rv.key}><Label className="text-xs">{rv.label}</Label>
                <Input value={vars[rv.key] ?? ""} onChange={(e) => setVars({ ...vars, [rv.key]: e.target.value })} className="mt-1" data-testid={`edit-var-${rv.key}`} /></div>
            ))}</div></div>}
        </div>
        <div className="flex justify-end gap-2 pt-4"><Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy} className="bg-vs-royal text-white" data-testid="edit-save">Save Changes</Button></div>
      </DialogContent>
    </Dialog>
  );
}

function NoteDialog({ open, onClose, title, onConfirm }) {
  const [note, setNote] = React.useState("");
  React.useEffect(() => { if (open) setNote(""); }, [open]);
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>{title}</DialogTitle><DialogDescription>Describe the changes requested. This is recorded in the activity trail.</DialogDescription></DialogHeader>
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} className="mt-2" data-testid="note-input" placeholder="Requested changes…" />
        <div className="flex justify-end gap-2 pt-3"><Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button disabled={!note.trim()} onClick={() => onConfirm(note)} className="bg-orange-600 text-white" data-testid="note-confirm">Confirm</Button></div>
      </DialogContent>
    </Dialog>
  );
}

function FinalReviewDialog({ open, onClose, validation, onConfirm, busy }) {
  const checks = validation?.checks || [];
  const blocking = validation?.blocking || [];
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Final Review</DialogTitle>
          <DialogDescription>All blocking checks must pass. Warnings are configuration items that stay marked in the document and do not block finalization.</DialogDescription></DialogHeader>
        <div className="space-y-2 mt-2" data-testid="final-review-checks">
          {checks.length === 0 && <p className="text-sm text-slate-400">No validation data available.</p>}
          {checks.map((c, i) => (
            <div key={i} className="flex items-start gap-2 text-sm">
              {c.ok ? <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" />
                : c.severity === "blocking" ? <Ban className="h-4 w-4 text-rose-600 shrink-0 mt-0.5" />
                : <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />}
              <div><span className={c.ok ? "text-slate-700" : c.severity === "blocking" ? "text-rose-700 font-medium" : "text-amber-700"}>{c.check}</span>
                {c.detail ? <div className="text-xs text-slate-400">{c.detail}</div> : null}</div>
            </div>
          ))}
        </div>
        <div className="rounded-lg bg-slate-50 border border-slate-100 p-3 mt-4 text-xs text-slate-500">
          Finalizing stores the PDF securely, snapshots Party A &amp; Party B and sets status <strong>READY_TO_SEND</strong>. No partner email or notification is sent in this step — partner delivery is Step 4.
        </div>
        <div className="flex justify-end gap-2 pt-4">
          <Button variant="outline" onClick={onClose}>Back</Button>
          <Button disabled={busy || blocking.length > 0} onClick={onConfirm} className="bg-emerald-600 text-white" data-testid="final-review-submit">
            {busy ? "Finalizing…" : blocking.length > 0 ? `${blocking.length} Blocking Issue(s)` : "Finalize Agreement"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function AgreementDetail() {
  const { agreementId } = useParams();
  const nav = useNavigate();
  const [data, setData] = React.useState(null);
  const [tab, setTab] = React.useState("Overview");
  const [editOpen, setEditOpen] = React.useState(false);
  const [noteOpen, setNoteOpen] = React.useState(false);
  const [reviewOpen, setReviewOpen] = React.useState(false);
  const [busy, setBusy] = React.useState(false);

  const load = React.useCallback(() => {
    api.get(`/admin/agreements/${agreementId}`).then((r) => setData(r.data)).catch((e) => { setData(false); toast.error(apiError(e.response?.data?.detail)); });
  }, [agreementId]);
  React.useEffect(() => { load(); }, [load]);

  const act = async (fn, msg) => { try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const transition = (to, note) => api.post(`/admin/agreements/${agreementId}/transition`, { to_status: to, note });
  const downloadPdf = async () => {
    try {
      const r = await api.get(`/admin/agreements/${agreementId}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      window.open(url, "_blank");
    } catch (e) { toast.error("Unable to open PDF"); }
  };
  const regenerate = () => act(() => api.post(`/admin/agreements/${agreementId}/regenerate`), "Regenerated from latest Party A / Party B data — new version created");
  const finalize = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/agreements/${agreementId}/submit`);
      toast.success("Agreement finalized (READY_TO_SEND). No partner email sent — delivery is Step 4.");
      setReviewOpen(false); load();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(d?.blocking ? `${d.message} ${d.blocking.join("; ")}` : apiError(d));
    } finally { setBusy(false); }
  };

  if (data === null) return <div><SkeletonRows rows={8} /></div>;
  if (data === false) return <EmptyState icon="FileSignature" title="Agreement not found" />;

  const a = data.agreement, pa = a.party_a || {}, pb = a.party_b || {};
  const st = a.status;
  const canEdit = data.editable;

  return (
    <div className="pb-6">
      <button onClick={() => nav("/app/agreements")} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-vs-royal mb-4" data-testid="agreement-back"><ArrowLeft className="h-4 w-4" />Agreements</button>

      <Card className="mb-5">
        <div className="flex flex-col lg:flex-row lg:items-start gap-5">
          <div className="flex items-start gap-4 min-w-0 flex-1">
            <div className="h-14 w-14 rounded-xl bg-vs-light flex items-center justify-center shrink-0"><FileSignature className="h-7 w-7 text-vs-royal" /></div>
            <div className="min-w-0">
              <h1 className="font-display font-bold text-xl text-slate-900 truncate" data-testid="agreement-title">{a.title}</h1>
              <div className="flex items-center gap-2 mt-1.5 text-xs text-slate-400 flex-wrap">
                <span className="font-semibold text-vs-royal">{a.agreement_number}</span><span>·</span>
                <span>{a.partner_name}</span><span>·</span><span>v{a.version}</span>
              </div>
              <div className="flex items-center gap-2 mt-3 flex-wrap">
                <AgreementStatusBadge status={st} /><AgreementTypeBadge type={a.agreement_type} />
                {data.immutable && <span className="inline-flex items-center gap-1 text-xs text-slate-500"><Lock className="h-3 w-3" />Immutable</span>}
              </div>
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mt-5 pt-5 border-t border-slate-100" data-testid="agreement-actions">
          {canEdit && <Button size="sm" variant="outline" onClick={() => setEditOpen(true)} data-testid="action-edit"><Edit3 className="h-3.5 w-3.5 mr-1" />Edit</Button>}
          {canEdit && <Button size="sm" variant="outline" onClick={regenerate} data-testid="action-regenerate"><RefreshCw className="h-3.5 w-3.5 mr-1" />Regenerate Party Data</Button>}
          {a.document && <Button size="sm" variant="outline" onClick={downloadPdf} data-testid="action-download">Download PDF</Button>}
          {["DRAFT", "APPROVED"].includes(st) && <Button size="sm" className="bg-emerald-600 text-white" onClick={() => setReviewOpen(true)} data-testid="action-submit"><ClipboardCheck className="h-3.5 w-3.5 mr-1" />Final Review & Finalize</Button>}
          {st === "DRAFT" && <Button size="sm" className="bg-vs-royal text-white" onClick={() => act(() => transition("IN_REVIEW"), "Sent for review")} data-testid="action-review"><Send className="h-3.5 w-3.5 mr-1" />Send for Review</Button>}
          {st === "IN_REVIEW" && <>
            <Button size="sm" className="bg-indigo-600 text-white" onClick={() => act(() => transition("APPROVED"), "Approved")} data-testid="action-approve"><CheckCircle2 className="h-3.5 w-3.5 mr-1" />Approve</Button>
            <Button size="sm" variant="outline" className="text-orange-600 border-orange-200" onClick={() => setNoteOpen(true)} data-testid="action-request-changes"><Edit3 className="h-3.5 w-3.5 mr-1" />Request Changes</Button>
          </>}
          {st === "CHANGES_REQUESTED" && <Button size="sm" className="bg-vs-royal text-white" onClick={() => act(() => transition("DRAFT"), "Reopened as draft")} data-testid="action-revise"><RotateCcw className="h-3.5 w-3.5 mr-1" />Revise Draft</Button>}
          {st === "APPROVED" && <Button size="sm" className="bg-violet-600 text-white" onClick={() => act(() => transition("READY_FOR_SIGNATURE"), "Ready for signature")} data-testid="action-ready"><PenLine className="h-3.5 w-3.5 mr-1" />Mark Ready for Signature</Button>}
          {st === "READY_FOR_SIGNATURE" && <span className="inline-flex items-center gap-1.5 text-sm text-violet-700 bg-violet-50 border border-violet-200 rounded-lg px-3 py-1.5"><PenLine className="h-4 w-4" />Signing workflow arrives in Phase 2</span>}
          {["DRAFT", "IN_REVIEW", "CHANGES_REQUESTED", "APPROVED", "READY_FOR_SIGNATURE", "READY_TO_SEND"].includes(st) &&
            <Button size="sm" variant="outline" className="text-rose-600 border-rose-200 ml-auto" onClick={() => act(() => transition("CANCELLED"), "Agreement cancelled")} data-testid="action-cancel"><Ban className="h-3.5 w-3.5 mr-1" />Cancel</Button>}
        </div>
      </Card>

      {st === "READY_TO_SEND" && <div className="mb-5 rounded-lg bg-emerald-50 border border-emerald-200 p-3 flex gap-2" data-testid="finalized-banner">
        <ShieldCheck className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" />
        <p className="text-xs text-emerald-800">Finalized{a.submitted_at ? ` on ${new Date(a.submitted_at).toLocaleString()}` : ""}{a.submitted_by ? ` by ${a.submitted_by}` : ""}. Final PDF securely stored{a.final_pdf?.filename ? ` (${a.final_pdf.filename}, SHA-256 ${a.final_pdf.sha256?.slice(0, 12)}…)` : ""}. Party A/B snapshots recorded. Partner dashboard delivery + email happen in Step 4 — nothing has been sent to the partner.</p></div>}

      {a.legal_note && <div className="mb-5 rounded-lg bg-amber-50 border border-amber-200 p-3 flex gap-2" data-testid="legal-note">
        <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" /><p className="text-xs text-amber-800">{a.legal_note}</p></div>}

      <div className="flex items-center gap-1 mb-5 overflow-x-auto border-b border-slate-100" data-testid="agreement-tabs">
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`tab-${t.toLowerCase()}`}
            className={`whitespace-nowrap px-3.5 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${tab === t ? "border-vs-royal text-vs-royal" : "border-transparent text-slate-500 hover:text-slate-700"}`}>{t}</button>
        ))}
      </div>

      {tab === "Overview" && (
        <div className="grid lg:grid-cols-2 gap-5">
          <Card title="Agreement Details"><div className="grid sm:grid-cols-2 gap-x-8">
            <Field label="Agreement Number" value={a.agreement_number} /><Field label="Type" value={(a.agreement_type || "").toUpperCase()} />
            <Field label="Status" value={st.replace(/_/g, " ")} /><Field label="Version" value={`v${a.version}`} />
            <Field label="Effective Date" value={a.effective_date} /><Field label="Expiry Date" value={a.expiry_date} />
            <Field label="Created By" value={a.created_by_name || a.created_by} /><Field label="Created" value={a.created_at ? new Date(a.created_at).toLocaleString() : null} />
          </div></Card>
          <Card title="Execution Status"><div className="space-y-3">
            <div className="flex items-center gap-2 text-sm text-slate-500"><ShieldCheck className="h-4 w-4 text-slate-400" />
              {a.document_hash ? <span className="text-emerald-700">Document sealed · {a.document_hash.slice(0, 16)}…</span> : "Not yet executed. Built-in signing (OTP identity, SHA-256 seal, execution certificate) arrives in Phase 2."}</div>
            <div className="text-xs text-slate-400">Signers: {(a.signers || []).length === 0 ? "None yet" : (a.signers || []).map((s) => s.name).join(", ")}</div>
          </div></Card>
        </div>
      )}

      {tab === "Parties" && (
        <div className="grid lg:grid-cols-2 gap-5">
          <Card title="Party A — VS-Solutions"><div className="grid sm:grid-cols-2 gap-x-8">
            <Field label="Legal Name" value={pa.legal_name} /><Field label="Short Name" value={pa.short_name} />
            <div className="sm:col-span-2"><Field label="Registered Address" value={pa.address} /></div>
            <Field label="Representative" value={pa.rep_name} /><Field label="Designation" value={pa.rep_designation} />
            <Field label="Email" value={pa.rep_email} /><Field label="PAN" value={pa.pan} /><Field label="GSTIN" value={pa.gstin} /><Field label="CIN" value={pa.cin} />
          </div>
          {!pa.address && <p className="text-xs text-amber-600 mt-2">Tip: complete the VS-Solutions company profile in System Settings so Party A details merge automatically.</p>}
          </Card>
          <Card title={`Party B — ${a.partner_name}`}><div className="grid sm:grid-cols-2 gap-x-8">
            <Field label="Legal Name" value={pb.legal_name} /><Field label="Short Name" value={pb.short_name} />
            <div className="sm:col-span-2"><Field label="Registered Address" value={pb.address} /></div>
            <Field label="Representative" value={pb.rep_name} /><Field label="Designation" value={pb.rep_designation} />
            <Field label="Email" value={pb.rep_email} /><Field label="PAN" value={pb.pan} /><Field label="GSTIN" value={pb.gstin} /><Field label="CIN" value={pb.cin} />
          </div></Card>
        </div>
      )}

      {tab === "Clauses" && (
        <Card title="Agreement Document" subtitle="Comprehensive document with sections, schedules and annexures. Party data is merged automatically.">
          {a.document ? (
            <div className="space-y-6" data-testid="agreement-content">
              {(a.document.sections || []).map((sec, si) => (
                <div key={si}>
                  <h3 className="font-display font-bold text-slate-900 mb-2">{si + 1}. {sec.title}</h3>
                  <div className="space-y-2">{(sec.items || []).map((it, ii) => (
                    <div key={ii} className="pl-4 border-l-2 border-slate-100">
                      {it.heading && <div className="text-sm font-semibold text-slate-700 mb-1">{it.heading}</div>}
                      {(it.paras || []).map((pp, pi) => <p key={pi} className="text-sm text-slate-600 leading-relaxed mb-1.5">{pp}</p>)}
                      {it.table && <div className="overflow-x-auto"><table className="w-full text-xs border border-slate-200 my-2"><thead><tr className="bg-slate-50">{it.table.head.map((h2, hi) => <th key={hi} className="px-2 py-1.5 text-left font-semibold text-slate-600 border-b border-slate-200">{h2}</th>)}</tr></thead><tbody>{it.table.rows.map((row, ri) => <tr key={ri} className="border-b border-slate-50">{row.map((c, ci) => <td key={ci} className="px-2 py-1.5 text-slate-600">{c}</td>)}</tr>)}</tbody></table></div>}
                    </div>
                  ))}</div>
                </div>
              ))}
              {[...(a.document.schedules || []), ...(a.document.annexures || [])].map((s, si) => (
                <div key={`x${si}`}>
                  <h3 className="font-display font-bold text-slate-900 mb-2">{s.title}</h3>
                  <div className="space-y-2">{(s.blocks || []).map((it, ii) => (
                    <div key={ii} className="pl-4 border-l-2 border-slate-100">
                      {(it.paras || []).map((pp, pi) => <p key={pi} className="text-sm text-slate-600 leading-relaxed mb-1.5">{pp}</p>)}
                      {it.table && <div className="overflow-x-auto"><table className="w-full text-xs border border-slate-200 my-2"><thead><tr className="bg-slate-50">{it.table.head.map((h2, hi) => <th key={hi} className="px-2 py-1.5 text-left font-semibold text-slate-600 border-b border-slate-200">{h2}</th>)}</tr></thead><tbody>{it.table.rows.map((row, ri) => <tr key={ri} className="border-b border-slate-50">{row.map((c, ci) => <td key={ci} className="px-2 py-1.5 text-slate-600">{c}</td>)}</tr>)}</tbody></table></div>}
                    </div>
                  ))}</div>
                </div>
              ))}
            </div>
          ) : <p className="text-sm text-slate-400">No document generated.</p>}
        </Card>
      )}

      {tab === "Versions" && (
        <Card title="Version History">
          {(data.versions || []).length === 0 ? <p className="text-sm text-slate-400">No versions recorded.</p> : (
            <div className="space-y-2" data-testid="version-list">{data.versions.map((v) => (
              <div key={v.version} className="flex items-center justify-between py-2 border-b border-slate-50">
                <div><span className="text-sm font-semibold text-slate-700">v{v.version}</span><span className="text-sm text-slate-500 ml-2">{v.change_note}</span></div>
                <div className="text-xs text-slate-400">{v.changed_by} · {v.changed_at ? new Date(v.changed_at).toLocaleString() : ""}</div>
              </div>
            ))}</div>
          )}
        </Card>
      )}

      {tab === "Activity" && (
        <Card title="Activity & Audit Trail">
          {(data.events || []).length === 0 ? <p className="text-sm text-slate-400">No recorded events.</p> : (
            <div className="space-y-3" data-testid="activity-timeline">{data.events.map((e, i) => (
              <div key={i} className="flex gap-3">
                <div className="h-2 w-2 rounded-full bg-vs-royal mt-1.5 shrink-0" />
                <div className="min-w-0"><div className="text-sm text-slate-700 capitalize">{(e.action || "").replace(/_/g, " ")}
                  {e.from_status || e.to_status ? <span className="text-slate-400"> {e.from_status || "—"} → {e.to_status || "—"}</span> : ""}</div>
                  {e.detail ? <div className="text-xs text-slate-500">{e.detail}</div> : null}
                  <div className="text-xs text-slate-400">{e.actor_name || "System"} · {e.timestamp ? new Date(e.timestamp).toLocaleString() : ""}</div></div>
              </div>
            ))}</div>
          )}
        </Card>
      )}

      <EditDialog open={editOpen} onClose={() => setEditOpen(false)} agreement={a} requiredVars={data.required_variables} onSaved={load} />
      <NoteDialog open={noteOpen} onClose={() => setNoteOpen(false)} title="Request Changes" onConfirm={async (note) => { await act(() => transition("CHANGES_REQUESTED", note), "Changes requested"); setNoteOpen(false); }} />
      <FinalReviewDialog open={reviewOpen} onClose={() => setReviewOpen(false)} validation={data.validation} onConfirm={finalize} busy={busy} />
    </div>
  );
}
